<template>
  <div class="sms-recharge-wrapper">
    <va-card class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 class="m-0 font-weight-bold">RECHARGE SMS BALANCE</h4>
          <p class="text-muted small m-0">Top up the prepaid SMS unit pool shared across the whole platform.</p>
        </div>
        <b-badge variant="success" class="p-2">PREPAID UNITS</b-badge>
      </div>

      <!-- Balance Panel -->
      <b-card class="mb-4 balance-panel">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="font-weight-bold m-0"><i class="fa fa-wallet mr-1"></i> CURRENT BALANCE</h6>
          <b-badge variant="info" class="p-2">SMS QUOTA</b-badge>
        </div>
        <b-row>
          <b-col md="3" sm="6" class="mb-3">
            <div class="balance-cell">
              <span class="balance-label">Remaining</span>
              <strong class="balance-value text-primary">{{ formatNumber(quota.remainingUnits) }}</strong>
              <small class="text-muted">units</small>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-3">
            <div class="balance-cell">
              <span class="balance-label">Total Purchased</span>
              <strong class="balance-value">{{ formatNumber(quota.totalPurchased) }}</strong>
              <small class="text-muted">units</small>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-3">
            <div class="balance-cell">
              <span class="balance-label">Total Used</span>
              <strong class="balance-value text-danger">{{ formatNumber(quota.totalUsed) }}</strong>
              <small class="text-muted">units</small>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-3">
            <div class="balance-cell">
              <span class="balance-label">Total Recharged</span>
              <strong class="balance-value text-success">{{ formatCurrency(quota.totalAmountRecharged) }}</strong>
              <small v-if="quota.lastRecharge" class="text-muted">
                Last: {{ formatCurrency(quota.lastRecharge.amount) }} on {{ formatDateTime(quota.lastRecharge.createdAt) }}
              </small>
              <small v-else class="text-muted">No recharge yet</small>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Recharge Form -->
      <b-card class="mb-4">
        <h6 class="font-weight-bold m-0 mb-3"><i class="fa fa-plus-circle mr-1"></i> ADD FUNDS</h6>
        <b-form @submit.prevent="submitRecharge">
          <b-row>
            <b-col md="4" sm="6" class="mb-3">
              <label class="small text-muted font-weight-bold text-uppercase">Amount (₹)</label>
              <b-form-input
                v-model.number="rechargeForm.amount"
                type="number"
                min="1"
                step="0.01"
                required
                placeholder="e.g. 500"
              />
              <small v-if="estimatedUnits > 0" class="text-muted">
                ≈ {{ formatNumber(estimatedUnits) }} units at ₹{{ quota.unitCost }} / unit
              </small>
            </b-col>
            <b-col md="4" sm="6" class="mb-3">
              <label class="small text-muted font-weight-bold text-uppercase">Remark (optional)</label>
              <b-form-input
                v-model.trim="rechargeForm.remark"
                type="text"
                placeholder="e.g. Monthly top-up"
              />
            </b-col>
            <b-col md="4" sm="12" class="mb-3 d-flex align-items-end">
              <b-button
                type="submit"
                variant="info"
                :disabled="rechargeSubmitting"
                class="ml-auto"
              >
                <b-spinner v-if="rechargeSubmitting" small class="mr-1"></b-spinner>
                {{ rechargeSubmitting ? 'Recharging...' : 'Recharge' }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-card>

      <!-- Recharge History -->
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="font-weight-bold m-0"><i class="fa fa-history mr-1"></i> RECHARGE HISTORY</h6>
          <span v-if="!historyLoading && historyTotal > 0" class="text-muted small">{{ historyRangeText }}</span>
        </div>

        <div v-if="historyLoading" class="text-center py-5">
          <b-spinner variant="info" label="Loading..."></b-spinner>
          <p class="text-muted mt-2">Loading recharge history...</p>
        </div>

        <div v-else-if="historyError" class="text-center py-5">
          <i class="fa fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
          <h5>Couldn't load recharge history</h5>
          <p class="text-muted small">{{ historyError }}</p>
          <b-button variant="info" size="sm" @click="fetchHistory">Try again</b-button>
        </div>

        <div v-else-if="historyRows.length === 0" class="text-center py-5 text-muted">
          <i class="fa fa-inbox fa-3x mb-3"></i>
          <h5>No recharges yet</h5>
          <p class="small">Recharges you add above will show up here.</p>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover table-striped border">
            <thead class="thead-dark">
              <tr>
                <th>Date &amp; Time</th>
                <th class="text-right">Amount</th>
                <th class="text-right">Units Added</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in historyRows" :key="row.id">
                <td class="small white-space-nowrap">
                  <strong>{{ formatDateTime(row.createdAt) }}</strong>
                </td>
                <td class="text-right font-weight-bold">{{ formatCurrency(row.amount) }}</td>
                <td class="text-right font-weight-bold text-success">+{{ formatNumber(row.unitsAdded) }}</td>
                <td>{{ row.remark || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="!historyLoading && !historyError && historyTotal > 0" class="d-flex justify-content-between align-items-center mt-3">
          <span class="small text-muted">{{ historyRangeText }}</span>
          <b-pagination
            v-model="historyPage"
            :total-rows="historyTotal"
            :per-page="historyLimit"
            size="sm"
            class="m-0"
            @change="goToHistoryPage"
          />
        </div>
      </b-card>
    </va-card>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SmsRecharge',
  data () {
    return {
      quotaLoading: false,
      quota: {
        remainingUnits: 0,
        totalPurchased: 0,
        totalUsed: 0,
        unitCost: 0.354,
        totalAmountRecharged: 0,
        lastRecharge: null
      },
      rechargeSubmitting: false,
      rechargeForm: { amount: null, remark: '' },
      historyLoading: false,
      historyError: '',
      historyRows: [],
      historyPage: 1,
      historyLimit: 10,
      historyTotal: 0,
      historyTotalPages: 1
    }
  },
  computed: {
    estimatedUnits () {
      const amount = Number(this.rechargeForm.amount)
      const unitCost = Number(this.quota.unitCost) || 0.354
      return Number.isFinite(amount) && amount > 0 ? Math.floor(amount / unitCost) : 0
    },
    historyRangeText () {
      if (!this.historyTotal) return '0 recharges'
      const start = ((this.historyPage - 1) * this.historyLimit) + 1
      const end = Math.min(this.historyPage * this.historyLimit, this.historyTotal)
      return `${this.formatNumber(start)}–${this.formatNumber(end)} of ${this.formatNumber(this.historyTotal)} recharges`
    }
  },
  mounted () {
    this.fetchQuota()
    this.fetchHistory()
  },
  methods: {
    getToken () {
      return localStorage.getItem('token') || localStorage.getItem('appToken') || ''
    },
    formatCurrency (val) {
      return '₹' + this.formatNumber(val)
    },
    formatNumber (val) {
      return new Intl.NumberFormat().format(Number(val) || 0)
    },
    formatDateTime (val) {
      if (!val) return '—'
      const d = new Date(val)
      return d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : '—'
    },
    errorMessage (err) {
      return (err && err.response && err.response.data && (err.response.data.message || err.response.data.msg)) ||
        (err && err.message) ||
        'Please check your connection and try again.'
    },
    normalizeQuota (data) {
      const root = data && typeof data === 'object' ? data : {}
      return {
        remainingUnits: Number(root.remainingUnits) || 0,
        totalPurchased: Number(root.totalPurchased) || 0,
        totalUsed: Number(root.totalUsed) || 0,
        unitCost: Number(root.unitCost) || 0.354,
        totalAmountRecharged: Number(root.totalAmountRecharged) || 0,
        lastRecharge: root.lastRecharge && typeof root.lastRecharge === 'object' ? root.lastRecharge : null
      }
    },
    fetchQuota () {
      this.quotaLoading = true
      const token = this.getToken()
      return axios.get('/v1/admin/messages/quota', {
        headers: { token, authorization: token }
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) {
          throw new Error(body.message || 'Could not load the SMS balance.')
        }
        this.quota = this.normalizeQuota(body.data)
      }).catch(err => {
        this.$dlg.alert(this.errorMessage(err), () => {}, { messageType: 'error' })
      }).finally(() => {
        this.quotaLoading = false
      })
    },
    fetchHistory () {
      this.historyLoading = true
      this.historyError = ''
      const token = this.getToken()
      return axios.get('/v1/admin/messages/quota/recharges', {
        headers: { token, authorization: token },
        params: { page: this.historyPage, limit: this.historyLimit }
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) {
          throw new Error(body.message || 'Could not load the recharge history.')
        }
        const data = body.data && typeof body.data === 'object' ? body.data : {}
        const rows = Array.isArray(data.rows) ? data.rows : []
        this.historyRows = rows.map((row, index) => ({
          id: `${row.createdAt || 'row'}-${index}`,
          amount: Number(row.amount) || 0,
          unitsAdded: Number(row.unitsAdded) || 0,
          remark: row.remark || '',
          createdAt: row.createdAt
        }))
        this.historyTotal = Number(data.total) || this.historyRows.length
        this.historyPage = Math.max(1, Number(data.page) || this.historyPage)
        this.historyLimit = Math.max(1, Number(data.limit) || this.historyLimit)
        this.historyTotalPages = Math.max(1, Number(data.totalPage) || Math.ceil(this.historyTotal / this.historyLimit))
      }).catch(err => {
        this.historyRows = []
        this.historyTotal = 0
        this.historyTotalPages = 1
        this.historyError = this.errorMessage(err)
      }).finally(() => {
        this.historyLoading = false
      })
    },
    goToHistoryPage (page) {
      if (page < 1 || page > this.historyTotalPages || page === this.historyPage) return
      this.historyPage = page
      this.fetchHistory()
    },
    submitRecharge () {
      const amount = Number(this.rechargeForm.amount)
      if (!Number.isFinite(amount) || amount <= 0) {
        this.$dlg.alert('Enter a recharge amount greater than zero.', () => {}, { messageType: 'error' })
        return
      }
      this.rechargeSubmitting = true
      const token = this.getToken()
      return axios.post('/v1/admin/messages/quota/recharge', {
        amount,
        remark: this.rechargeForm.remark
      }, {
        headers: { token, authorization: token }
      }).then(response => {
        const body = response && response.data ? response.data : {}
        if (body.status === false) {
          throw new Error(body.message || 'The recharge could not be completed.')
        }
        this.quota = this.normalizeQuota(body.data)
        this.rechargeForm = { amount: null, remark: '' }
        const unitsAdded = this.quota.lastRecharge ? this.quota.lastRecharge.unitsAdded : 0
        this.$dlg.alert(
          `Added ${this.formatNumber(unitsAdded)} units to the SMS balance.`,
          () => {},
          { messageType: 'success', title: 'Recharge successful' }
        )
        this.historyPage = 1
        this.fetchHistory()
      }).catch(err => {
        this.$dlg.alert(this.errorMessage(err), () => {}, { messageType: 'error', title: 'Recharge failed' })
      }).finally(() => {
        this.rechargeSubmitting = false
      })
    }
  }
}
</script>

<style scoped>
.sms-recharge-wrapper {
  padding: 15px;
}
.balance-panel {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}
.balance-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.balance-label {
  color: #6c757d;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.balance-value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  color: #173f35;
}
.white-space-nowrap {
  white-space: nowrap;
}
</style>
